import { editTableCell } from '@/store/thunks/UpdateTable';
import { EvidenceCard } from '@/components/table/EvidenceCard';
import { getCurrentThread } from '@/store/reducers/UIState';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import { TableCell } from '@/models/Table';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { TextareaAutosize as BaseTextareaAutosize } from '@mui/base/TextareaAutosize';
import { Box, Button, Modal, styled, Typography } from '@mui/material';
import {
  ChangeEvent,
  FC,
  MouseEventHandler,
  ReactNode,
  SVGProps,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Paper from '@mui/material/Paper';
import UndoIcon from '@mui/icons-material/Undo';

type CellProps = {
  cell: TableCell;
  cellKey: string;
  onEditCell?: (cellKey: string, editedValue: string) => void;
  isReadOnly: boolean;
};

export const Cell = ({ cell, cellKey, onEditCell }: CellProps): ReactNode => {
  const textAreaRef = useRef<any>();
  const [isEditing, setIsEditing] = useState(false);
  const { isReadOnly } = useContext(PermissionsContext);
  const [userEditedString, setUserEditedString] = useState(
    cell.editedValue || cell.displayValue || '',
  );

  const evidenceId = cell.metadata?.evidenceId;
  const dispatch = useAppDispatch();
  const currentThread = useAppSelector(getCurrentThread);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleOpenEditModal = () => setIsEditModalOpen(true);
  const handleCloseEditModal = () => setIsEditModalOpen(false);

  const handleResetEdit: MouseEventHandler = async () => {
    if (!currentThread) {
      console.error('Current thread is null, cannot reset edit.');
      return; // Exit early if currentThread is null
    }

    await dispatch(
      editTableCell({
        currentThreadId: currentThread.id,
        cellKey: cellKey,
        editedValue: null,
      }),
    );
    setUserEditedString(cell.displayValue || '');
    handleCloseEditModal();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isEditing && textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }, 4);
    return () => clearTimeout(timer);
  }, [isEditing]);

  const onChangeEdit = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setUserEditedString(event.target.value);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const onBlurEdit = () => {
    setIsEditing(false);
    const trimmedEditedString = userEditedString.trim();
    if (onEditCell && trimmedEditedString !== cell.displayValue) {
      onEditCell(cellKey, trimmedEditedString);
    }
  };

  return (
    <Box position="relative" height={1}>
      {isEditing && !isReadOnly ? (
        <Textarea
          ref={textAreaRef}
          aria-label="Edit cell textarea"
          value={userEditedString}
          onBlur={onBlurEdit}
          onChange={onChangeEdit}
        />
      ) : (
        <Box p={0.75} height={1}>
          <Typography variant="body2" onClick={handleEditClick}>
            {cell.editedValue || cell.displayValue}
          </Typography>
          {cell.editedValue && (
            <>
              <EditedButton
                onClick={handleOpenEditModal}
                size="small"
                data-testid="edited-cell-icon"
              >
                <EditedIcon />
              </EditedButton>
              <CellRollbackModal
                open={isEditModalOpen}
                onClose={handleCloseEditModal}
              >
                <RollbackPaper>
                  <Typography variant="h5" component="h2" color="white">
                    Original Value
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2.5, mb: 2 }}>
                    {cell.displayValue}
                  </Typography>
                  <ModalFooter>
                    <UndoButton onClick={handleResetEdit}>
                      <StyledUndoIcon />
                    </UndoButton>
                    Reset Edit
                  </ModalFooter>
                </RollbackPaper>
              </CellRollbackModal>
            </>
          )}
          {!!evidenceId && (
            <EvidenceCard
              evidenceId={evidenceId}
              anchor={<EvidenceText>Evidence</EvidenceText>}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const EvidenceText = styled('span')`
  color: ${({ theme }) => theme.palette.secondary.main};
  text-decoration: 2px underline dotted;
  text-underline-offset: 3px;
`;

export const EditIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 18 18"
    width="16"
    height="16"
    {...props}
  >
    <circle cx="9" cy="9" r="8" />
    <path
      d="M8.8,3.1l-1-1.7-2.4,1.4V0h-2v2.8L1,1.4l-1,1.7,2.4,1.4-2.4,1.4,1,1.7,2.4-1.4v2.8h2v-2.8l2.4,1.4,1-1.7-2.4-1.4,2.4-1.4Z"
      transform="translate(4,4)"
    />
  </svg>
);

const EditedIcon = styled(EditIcon)`
  fill: ${({ theme }) => theme.color['pink-100'].hex};
  & circle {
    fill: #faf2e9;
  }
`;

const StyledUndoIcon = styled(UndoIcon)`
  fill: ${({ theme }) => theme.color['green-60'].hex};
`;

const EditedButton = styled(Button)`
  margin-left: auto;
  min-width: unset;
`;

const UndoButton = styled(Button)`
  min-width: auto;
  padding-right: ${({ theme }) => theme.spacing(1.5)};
`;

const CellRollbackModal = styled(Modal)`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(3)};
  box-shadow: 0 0 10px 4px rgba(20, 51, 60, 0.5);
`;

const ModalFooter = styled('div')`
  align-items: center;
  background-color: ${({ theme }) => theme.color['dark-teal-100'].hex};
  border-radius: 0 0 8px 8px;
  color: white;
  display: flex;
  justify-content: flex-start;
  margin: 0 -12px -12px -12px;
  padding: ${({ theme }) => theme.spacing(1)};
`;

export const RollbackPaper = styled(Paper)`
  color: white;
  background-color: #032629; // TODO: replace once varnish updates dark-blue-100 in theme
  max-width: 400px;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing(1.5)};
`;

export const Textarea = styled(BaseTextareaAutosize)(
  ({ theme }) => `
    background-color: ${theme.color['dark-teal-100'].hex};
    border: 0;
    box-sizing: border-box;
    color: ${theme.color['cream-100'].hex};
    font-family: Manrope, Arial, sans-serif;
    font-size: 1rem;
    height: 100%;
    letter-spacing: 0rem;
    min-height: 4em;
    padding: ${theme.spacing(1)};
    resize: none;
    width: 100%;
   `,
);
